/* Job Profiles — full CRUD page with attached resumes + ranked scoring. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Briefcase, Plus, Trash2, Target, Pencil, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["open", "on_hold", "closed"];

export default function JobProfiles() {
  const [list, setList] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [scoring, setScoring] = useState({});  // {profileId: {rid: score}}

  const refresh = async () => {
    const [r1, r2] = await Promise.all([
      api.get("/job-profiles"),
      api.get("/resumes"),
    ]);
    setList(r1.data || []);
    setResumes(r2.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this job profile?")) return;
    await api.delete(`/job-profiles/${id}`);
    toast.success("Deleted"); refresh();
  };

  const rankCandidates = async (profile) => {
    toast.info(`Scoring ${Math.min(8, resumes.length)} resumes against ${profile.title}…`);
    setScoring({ ...scoring, [profile.id]: { loading: true } });
    const scores = {};
    for (const r of resumes.slice(0, 8)) {
      try {
        const res = await api.post(`/resumes/${r.id}/score`, { job_profile_id: profile.id });
        scores[r.id] = res.data.score_result.score;
      } catch { scores[r.id] = null; }
    }
    setScoring({ ...scoring, [profile.id]: scores });
    toast.success("Ranking complete");
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Briefcase className="size-3.5" /> Job Profiles</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Open roles</h1>
          <p className="text-muted-foreground mt-1 text-sm">Create a role, score every candidate, rank the best-fit.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-2 lift" data-testid="jp-new-button"><Plus className="size-4" /> New job profile</Button>
      </div>

      {list.length === 0 ? (
        <Card className="border-dashed border-border">
          <CardContent className="p-10 text-center">
            <Briefcase className="size-12 text-muted-foreground mx-auto opacity-50" />
            <div className="display font-bold text-lg mt-3">No job profiles yet</div>
            <div className="text-sm text-muted-foreground">Describe an open role once — score every resume against it in a click.</div>
            <Button className="mt-4 gap-2" onClick={() => setCreating(true)}><Plus className="size-4" /> Create first profile</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((p) => {
            const scoreMap = scoring[p.id] || {};
            const ranked = scoreMap && !scoreMap.loading
              ? resumes.map((r) => ({ r, score: scoreMap[r.id] })).filter((x) => typeof x.score === "number").sort((a, b) => b.score - a.score)
              : [];
            return (
              <Card key={p.id} className="border-border" data-testid={`jp-card-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="display font-bold text-lg truncate">{p.title || "(untitled)"}</div>
                      <div className="text-xs text-muted-foreground truncate">{p.industry} · {p.experience_min_years}-{p.experience_max_years} yrs</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`jp-edit-${p.id}`}><Pencil className="size-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(p.id)} className="text-destructive" data-testid={`jp-del-${p.id}`}><Trash2 className="size-4" /></Button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(p.required_skills || []).slice(0, 8).map((s, i) => <span key={i} className="chip bg-primary/10 text-primary border-primary/30">{s}</span>)}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => rankCandidates(p)} disabled={scoreMap.loading} className="gap-1.5" data-testid={`jp-rank-${p.id}`}>
                      {scoreMap.loading ? <Loader2 className="size-3.5 animate-spin" /> : <Target className="size-3.5" />} Rank candidates
                    </Button>
                    <span className="text-xs text-muted-foreground">{resumes.length} resumes in library</span>
                  </div>
                  {ranked.length > 0 && (
                    <div className="mt-3 border-t border-border pt-3">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Top matches</div>
                      <div className="space-y-1.5">
                        {ranked.slice(0, 5).map(({ r, score }) => (
                          <a key={r.id} href={`/editor/${r.id}`} className="flex items-center justify-between text-xs hover:bg-secondary px-1.5 py-1 rounded-md">
                            <span className="truncate font-semibold">{r.data?.name || r.title}</span>
                            <span className={`font-black ${score >= 80 ? "text-emerald-500" : score >= 60 ? "text-sky-500" : score >= 40 ? "text-amber-500" : "text-rose-500"}`}>{score}/100</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <ProfileDialog
          initial={editing || { title: "", summary: "", responsibilities: "", required_skills: [], preferred_skills: [], experience_min_years: 0, experience_max_years: 20, industry: "" }}
          onClose={() => { setEditing(null); setCreating(false); refresh(); }}
        />
      )}
    </div>
  );
}

function ProfileDialog({ initial, onClose }) {
  const [p, setP] = useState({
    ...initial,
    required_skills: (initial.required_skills || []).join(", "),
    preferred_skills: (initial.preferred_skills || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: p.title, summary: p.summary, responsibilities: p.responsibilities, industry: p.industry,
        required_skills: (p.required_skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        preferred_skills: (p.preferred_skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        experience_min_years: Number(p.experience_min_years) || 0,
        experience_max_years: Number(p.experience_max_years) || 20,
        public: !!p.public,
        location: p.location || "",
        department: p.department || "",
        employment_type: p.employment_type || "Full-time",
      };
      if (p.id) await api.put(`/job-profiles/${p.id}`, body);
      else await api.post("/job-profiles", body);
      toast.success("Saved");
      onClose();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px]" data-testid="jp-dialog">
        <DialogHeader><DialogTitle>{p.id ? "Edit job profile" : "New job profile"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Job title" value={p.title || ""} onChange={(e) => setP({ ...p, title: e.target.value })} data-testid="jp-title" />
          <Textarea rows={2} placeholder="Role summary" value={p.summary || ""} onChange={(e) => setP({ ...p, summary: e.target.value })} data-testid="jp-summary" />
          <Textarea rows={3} placeholder="Key responsibilities" value={p.responsibilities || ""} onChange={(e) => setP({ ...p, responsibilities: e.target.value })} />
          <Input placeholder="Required skills (comma-separated)" value={p.required_skills || ""} onChange={(e) => setP({ ...p, required_skills: e.target.value })} data-testid="jp-required" />
          <Input placeholder="Preferred skills (comma-separated)" value={p.preferred_skills || ""} onChange={(e) => setP({ ...p, preferred_skills: e.target.value })} />
          <div className="grid grid-cols-3 gap-2">
            <Input type="number" placeholder="Min years" value={p.experience_min_years || 0} onChange={(e) => setP({ ...p, experience_min_years: e.target.value })} />
            <Input type="number" placeholder="Max years" value={p.experience_max_years || 20} onChange={(e) => setP({ ...p, experience_max_years: e.target.value })} />
            <Input placeholder="Industry / Domain" value={p.industry || ""} onChange={(e) => setP({ ...p, industry: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Location (e.g. Bangalore / Remote)" value={p.location || ""} onChange={(e) => setP({ ...p, location: e.target.value })} data-testid="jp-location" />
            <Input placeholder="Department" value={p.department || ""} onChange={(e) => setP({ ...p, department: e.target.value })} />
            <Input placeholder="Employment type" value={p.employment_type || "Full-time"} onChange={(e) => setP({ ...p, employment_type: e.target.value })} />
          </div>
          <div className="flex items-center gap-3 p-3 border border-border rounded-md bg-secondary/30" data-testid="jp-public-row">
            <input type="checkbox" checked={!!p.public} onChange={(e) => setP({ ...p, public: e.target.checked })} data-testid="jp-public" />
            <div className="flex-1">
              <div className="text-sm font-semibold">Publish on public Career page</div>
              <div className="text-[11px] text-muted-foreground">When ON, this role shows at <code>/careers</code> and accepts public applications. Disable to keep it internal-only.</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="jp-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
